<template>
  <!-- Her zaman görünür mod: metin etiketin altına akar (FIELD_INFO_COLLAPSIBLE = false) -->
  <div class="custom-ui-field-info" v-if="text && !collapsible && !popover">
    <ui-line-message
      class="custom-ui-field-info-message"
      size="small"
      :message="text"
      iconType="info"
      icon="fa-light fa-info-circle"
    />
  </div>

  <!--
    Varsayılan mod: ikon etiketin yanında durur, metin body'ye teleport edilen yüzen kutuda
    gösterilir. Kutu yerleşime girmediği için alan yüksekliği değişmez, grid bozulmaz.
    Satır içi kullanımda üzerine gelince, buton gruplarında (popover) tıklayınca açılır.
  -->
  <div
    class="custom-ui-field-info anchor"
    v-else-if="text"
    @mouseenter="onEnter"
    @mouseleave="onLeave"
  >
    <button
      ref="toggleRef"
      type="button"
      class="custom-ui-field-info-toggle"
      :class="{ open }"
      :aria-expanded="open"
      :title="t('admin_global_info')"
      @click="onClick"
      @focus="onEnter"
      @blur="hide"
    >
      <i class="fa-light fa-info-circle" />
    </button>
    <Teleport to="body">
      <div
        v-if="open"
        ref="boxRef"
        class="custom-ui-field-info-box"
        :style="boxStyle"
        @mouseenter="onEnter"
        @mouseleave="onLeave"
        @click.stop
      >
        <ui-line-message size="small" :message="text" iconType="info" />
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
  import { FIELD_INFO_COLLAPSIBLE } from '@/data/fieldInfo'
  import type { IUIFieldInfo } from '@/types/Components/UIKit'
  import { UseI18n } from '@/utils-ts'
  import { nextTick, onBeforeUnmount, ref } from 'vue'

  const {
    text = '',
    collapsible = FIELD_INFO_COLLAPSIBLE,
    popover = false,
  } = defineProps<IUIFieldInfo>()

  const { t } = UseI18n()

  const open = ref<boolean>(false)
  const pinned = ref<boolean>(false)
  const toggleRef = ref<HTMLElement | null>(null)
  const boxRef = ref<HTMLElement | null>(null)
  const boxStyle = ref<Record<string, string>>({})

  const GAP = 8
  let closeTimer: ReturnType<typeof setTimeout> | null = null

  // Kutu body'ye teleport edildiği için konum viewport koordinatlarıyla hesaplanır:
  // ikonun altına açılır, sağa/sola taşarsa içeri çekilir, aşağıda yer yoksa yukarı çıkar.
  const place = async () => {
    const anchor = toggleRef.value
    if (!anchor) return

    const rect = anchor.getBoundingClientRect()
    const maxWidth = Math.min(420, window.innerWidth - GAP * 2)

    boxStyle.value = {
      top: `${rect.bottom + GAP}px`,
      left: `${rect.left}px`,
      maxWidth: `${maxWidth}px`,
    }

    // Kutu içeriğe göre büyüdüğü için kesin konum ancak render sonrası ölçülebilir.
    await nextTick()
    const box = boxRef.value
    if (!box) return

    const { offsetWidth: width, offsetHeight: height } = box

    let left = rect.left
    if (left + width > window.innerWidth - GAP) left = window.innerWidth - GAP - width
    if (left < GAP) left = GAP

    let top = rect.bottom + GAP
    if (top + height > window.innerHeight - GAP && rect.top - GAP - height > GAP) {
      top = rect.top - GAP - height
    }

    boxStyle.value = { ...boxStyle.value, top: `${top}px`, left: `${left}px` }
  }

  const onOutsideClick = (event: MouseEvent) => {
    const target = event.target as Node
    if (toggleRef.value?.contains(target) || boxRef.value?.contains(target)) return
    hide()
  }

  const onEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape') hide()
  }

  const bind = (add: boolean) => {
    const win = add ? window.addEventListener : window.removeEventListener
    const doc = add ? document.addEventListener : document.removeEventListener
    win('scroll', place, true)
    win('resize', place)
    doc('click', onOutsideClick as EventListener)
    doc('keydown', onEscape as EventListener)
  }

  const clearTimer = () => {
    if (closeTimer) {
      clearTimeout(closeTimer)
      closeTimer = null
    }
  }

  const show = () => {
    clearTimer()
    if (open.value) {
      place()
      return
    }
    open.value = true
    bind(true)
    place()
  }

  function hide() {
    clearTimer()
    if (!open.value) return
    open.value = false
    pinned.value = false
    bind(false)
  }

  // Buton gruplarında (popover) tıklamayla açılır; form alanlarında üzerine gelince.
  const onEnter = () => {
    if (popover) return
    show()
  }

  // İmleç ikondan kutuya geçerken kapanmasın diye küçük bir gecikme bırakılır.
  const onLeave = () => {
    if (popover || pinned.value) return
    clearTimer()
    closeTimer = setTimeout(hide, 120)
  }

  const onClick = () => {
    if (open.value && (popover || pinned.value)) {
      hide()
      return
    }
    pinned.value = true
    show()
  }

  onBeforeUnmount(() => {
    clearTimer()
    bind(false)
  })
</script>

<style lang="scss" scoped>
  // Kök eleman yerleşime girmez; ikon etiketin yanındaki boşluğa oturur.
  .custom-ui-field-info {
    display: contents;

    .custom-ui-field-info-message {
      flex-basis: 100%;
      width: 100%;
      margin-top: 0.25rem;
    }

    &.anchor {
      display: inline-flex;
      align-items: center;
      flex: 0 0 auto;
    }
  }

  .custom-ui-field-info-toggle {
    @include flexible($align: center, $justify: center);
    width: 1rem;
    height: 1rem;
    padding: 0;
    border: none;
    background: transparent;
    color: $info;
    cursor: pointer;
    font-size: 0.875rem;
    line-height: 1;
    transition: color 0.2s ease;

    &:hover,
    &.open {
      color: $brandColor;
    }
  }

  // body'ye teleport edildiği için üst seviyede tanımlanır
  .custom-ui-field-info-box {
    position: fixed;
    z-index: 9999;
    box-sizing: border-box;
    width: max-content;
    padding: 0.75rem 1rem;
    background: $white;
    border: 1px solid $border-color;
    border-radius: 6px;
    box-shadow: $default-shadow;
  }
</style>
